<!--
  ContactForm.vue - Reusable contact form component

  Shared between the public contact page and the dashboard support drawer.
  Handles validation, submission, success/error states.

  Props:
  - initialValues: Pre-fill form fields (e.g. from auth store)
  - extraPayload: Additional data to send with the form (e.g. userId, userTier)
  - compact: Use compact styling for drawer context
  - successMessage: Custom success description text
  - useRouterLink: Use RouterLink instead of <a> for privacy policy link

  Emits:
  - success: When form submission succeeds
-->

<template>
  <!-- Success State -->
  <div v-if="showSuccess" :class="compact ? 'text-center py-8' : 'text-center py-12'">
    <div
      :class="[
        'mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center',
        compact ? 'w-16 h-16' : 'w-20 h-20 mb-6'
      ]"
    >
      <CheckIcon :class="compact ? 'w-8 h-8 text-green-600' : 'w-10 h-10 text-green-600'" />
    </div>
    <h3 :class="[
      'font-semibold text-slate-900 mb-2',
      compact ? 'text-lg font-outfit' : 'text-2xl font-bold text-text-primary'
    ]">
      Mensaje enviado
    </h3>
    <p :class="compact ? 'text-sm text-slate-600 mb-6' : 'text-text-secondary mb-8'">
      {{ successMessage }}
    </p>
    <button
      type="button"
      @click="handleSendAnother"
      :class="[
        'inline-flex items-center gap-2 font-medium rounded-lg transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2',
        compact
          ? 'px-4 py-2 bg-accent-600 text-white text-sm hover:bg-accent-700'
          : 'px-6 py-3 bg-accent-600 text-white font-semibold rounded-xl hover:bg-accent-700'
      ]"
    >
      <PaperAirplaneIcon v-if="compact" class="w-4 h-4" />
      Enviar otro mensaje
    </button>
  </div>

  <!-- Form -->
  <div v-else>
    <slot name="header" />

    <!-- Error Message -->
    <div
      v-if="errorMessage"
      :class="compact ? 'mb-4 bg-red-50 border border-red-200 rounded-xl p-3' : 'mb-6 bg-red-50 border border-red-200 rounded-2xl p-4'"
    >
      <div class="flex gap-2">
        <ExclamationTriangleIcon class="h-5 w-5 text-red-400 flex-shrink-0" />
        <p class="text-sm text-red-700">{{ errorMessage }}</p>
      </div>
    </div>

    <Form
      ref="formRef"
      @submit="handleSubmit"
      :validation-schema="contactSchema"
      :initial-values="mergedInitialValues"
      v-slot="{ errors, isSubmitting: formSubmitting }"
    >
      <div :class="compact ? 'space-y-4' : 'space-y-6'">
        <!-- Name & Email: side by side on page, stacked in drawer -->
        <div :class="compact ? '' : 'grid md:grid-cols-2 gap-4'">
          <!-- Name -->
          <div :class="compact ? 'mb-4' : ''">
            <label :for="fieldId('name')" :class="labelClass">
              Nombre completo *
            </label>
            <Field
              name="name"
              type="text"
              :id="fieldId('name')"
              placeholder="Tu nombre"
              autocomplete="name"
              :class="[inputBaseClass, errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200']"
            />
            <ErrorMessage name="name" :class="errorClass" />
          </div>

          <!-- Email -->
          <div>
            <label :for="fieldId('email')" :class="labelClass">
              Email *
            </label>
            <Field
              name="email"
              type="email"
              :id="fieldId('email')"
              placeholder="tu@email.com"
              autocomplete="email"
              :class="[inputBaseClass, errors.email ? 'border-red-300 bg-red-50' : 'border-slate-200']"
            />
            <ErrorMessage name="email" :class="errorClass" />
          </div>
        </div>

        <!-- Phone & Subject: side by side on page, stacked in drawer -->
        <div :class="compact ? '' : 'grid md:grid-cols-2 gap-4'">
          <!-- Phone -->
          <div :class="compact ? 'mb-4' : ''">
            <label :for="fieldId('phone')" :class="labelClass">
              Teléfono
              <span class="text-slate-400 font-normal">(opcional)</span>
            </label>
            <Field
              name="phone"
              type="tel"
              :id="fieldId('phone')"
              placeholder="+34 600 000 000"
              autocomplete="tel"
              :class="[inputBaseClass, errors.phone ? 'border-red-300 bg-red-50' : 'border-slate-200']"
            />
            <ErrorMessage name="phone" :class="errorClass" />
          </div>

          <!-- Subject -->
          <div>
            <label :for="fieldId('subject')" :class="labelClass">
              Asunto *
            </label>
            <Field
              as="select"
              name="subject"
              :id="fieldId('subject')"
              :class="[inputBaseClass, 'bg-white', errors.subject ? 'border-red-300 bg-red-50' : 'border-slate-200']"
            >
              <option value="">Selecciona un asunto</option>
              <option v-for="s in CONTACT_SUBJECTS" :key="s.value" :value="s.value">
                {{ s.label }}
              </option>
            </Field>
            <ErrorMessage name="subject" :class="errorClass" />
          </div>
        </div>

        <!-- Message -->
        <div>
          <label :for="fieldId('message')" :class="labelClass">
            Mensaje *
          </label>
          <Field
            as="textarea"
            name="message"
            :id="fieldId('message')"
            :rows="compact ? 4 : 5"
            placeholder="Describe tu consulta..."
            :class="[inputBaseClass, 'resize-none', errors.message ? 'border-red-300 bg-red-50' : 'border-slate-200']"
          />
          <div class="flex justify-between mt-1">
            <ErrorMessage name="message" :class="errorClass" />
            <span class="text-xs text-slate-400">20-2000 caracteres</span>
          </div>
        </div>

        <!-- GDPR Consent -->
        <div :class="compact ? 'bg-slate-50 rounded-lg p-3' : 'bg-slate-50 rounded-xl p-4'">
          <div :class="compact ? 'flex items-start gap-2.5' : 'flex items-start gap-3'">
            <Field
              name="gdprConsent"
              type="checkbox"
              :id="fieldId('gdpr')"
              :value="true"
              :unchecked-value="false"
              :class="[
                'mt-0.5 text-accent-600 border-slate-300 rounded focus:ring-accent-500',
                compact ? 'w-4 h-4' : 'w-5 h-5'
              ]"
            />
            <div>
              <label :for="fieldId('gdpr')" :class="compact ? 'text-xs text-slate-700' : 'text-sm text-text-primary'">
                He leído y acepto la
                <RouterLink v-if="useRouterLink" to="/politica-privacidad" class="text-accent-600 hover:underline">
                  política de privacidad
                </RouterLink>
                <a v-else href="/politica-privacidad" target="_blank" class="text-accent-600 hover:underline">
                  política de privacidad
                </a>
                y el tratamiento de mis datos para gestionar esta consulta. *
              </label>
              <ErrorMessage name="gdprConsent" :class="['block mt-1', errorClass]" />
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          :disabled="formSubmitting"
          :class="[
            'w-full flex items-center justify-center gap-2 font-medium transition-all',
            compact
              ? 'px-4 py-3 rounded-lg'
              : 'px-6 py-4 rounded-xl font-semibold',
            formSubmitting
              ? compact
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
              : compact
                ? 'bg-accent-600 text-white hover:bg-accent-700'
                : 'bg-gradient-to-r from-accent-600 to-teal-500 text-white shadow-lg shadow-accent-500/25 hover:shadow-xl hover:scale-[1.02]'
          ]"
        >
          <PaperAirplaneIcon :class="compact ? 'w-4 h-4' : 'w-5 h-5'" />
          {{ formSubmitting ? 'Enviando...' : 'Enviar mensaje' }}
        </button>
      </div>
    </Form>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { RouterLink } from 'vue-router'
import { Form, Field, ErrorMessage } from 'vee-validate'
import * as yup from 'yup'
import axios from 'axios'
import { emailSchema, nameSchema } from '@/utils/validationSchemas'
import { CONTACT_SUBJECTS, CONTACT_SUBJECT_VALUES } from '@/constants/contactSubjects'
import {
  CheckIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon
} from '@heroicons/vue/24/outline'

defineOptions({ name: 'ContactForm' })

const props = defineProps({
  initialValues: {
    type: Object,
    default: () => ({})
  },
  extraPayload: {
    type: Object,
    default: () => ({})
  },
  compact: {
    type: Boolean,
    default: false
  },
  successMessage: {
    type: String,
    default: 'Hemos recibido tu mensaje y te responderemos lo antes posible.'
  },
  useRouterLink: {
    type: Boolean,
    default: false
  },
})

const emit = defineEmits(['success'])

// Refs
const formRef = ref(null)
const showSuccess = ref(false)
const errorMessage = ref('')

// Generate unique field IDs to avoid conflicts when used multiple times on a page
const idPrefix = computed(() => props.compact ? 'support' : 'contact')
const fieldId = (name) => `${idPrefix.value}-${name}`

// Merge default values with provided initial values
const mergedInitialValues = computed(() => ({
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  gdprConsent: false,
  ...props.initialValues
}))

// Styling computed
const labelClass = computed(() =>
  props.compact
    ? 'block text-sm font-medium text-slate-900 mb-1.5'
    : 'block text-sm font-medium text-text-primary mb-2'
)

const inputBaseClass = computed(() =>
  props.compact
    ? 'w-full px-3 py-2.5 text-sm border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500'
    : 'w-full px-4 py-3 border rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500'
)

const errorClass = computed(() =>
  props.compact ? 'mt-1 text-xs text-red-600' : 'mt-1 text-sm text-red-600'
)

// Validation schema — reuses shared field schemas from validationSchemas.js
const contactSchema = yup.object({
  name: nameSchema.label('Nombre completo'),

  email: emailSchema,

  phone: yup
    .string()
    .spanishPhone()
    .nullable(),

  subject: yup
    .string()
    .required('Selecciona un asunto')
    .oneOf(CONTACT_SUBJECT_VALUES, 'Asunto no válido'),

  message: yup
    .string()
    .required('El mensaje es obligatorio')
    .min(20, 'Mínimo 20 caracteres')
    .max(2000, 'Máximo 2000 caracteres'),

  gdprConsent: yup
    .boolean()
    .oneOf([true], 'Debes aceptar la política de privacidad')
    .required('Debes aceptar la política de privacidad')
})

// Handle form submission
const handleSubmit = async (values, { resetForm }) => {
  try {
    errorMessage.value = ''

    const payload = {
      name: values.name,
      email: values.email,
      phone: values.phone || null,
      subject: values.subject,
      message: values.message,
      gdprConsent: values.gdprConsent,
      ...props.extraPayload
    }

    const response = await axios.post('/api/contact/send', payload)

    if (response.data.success) {
      showSuccess.value = true
      resetForm()
      emit('success')
    }
  } catch (error) {
    console.error('Contact form error:', error)

    if (error.response?.status === 429) {
      errorMessage.value = 'Demasiados mensajes. Espera unos minutos.'
    } else if (error.response?.data?.message) {
      errorMessage.value = error.response.data.message
    } else {
      errorMessage.value = 'Error al enviar. Inténtalo de nuevo.'
    }
  }
}

// Send another message
const handleSendAnother = () => {
  showSuccess.value = false
  errorMessage.value = ''
}

// Expose for parent components
defineExpose({
  resetState: handleSendAnother
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Focus states */
input:focus,
select:focus,
textarea:focus {
  outline: none;
}

/* Checkbox styling */
input[type="checkbox"] {
  cursor: pointer;
}

</style>
